import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as VerifiedIcon,
} from '@mui/icons-material';
import { PersonalRecord, User } from '../../types';
import { avatarService } from '../../services/avatarService';
import { useAuth } from '../../context/AuthContext';
import AddEditPRDialog from '../profile/AddEditPRDialog';
import { formatPRTimeDisplay } from '../../utils/prTimeFormat';

interface AthletePREditorProps {
  open: boolean;
  onClose: () => void;
  athlete: User;
  teamId: string;
}

const AthletePREditor: React.FC<AthletePREditorProps> = ({
  open,
  onClose,
  athlete,
  teamId,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prs, setPRs] = useState<PersonalRecord[]>([]);
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [editingPR, setEditingPR] = useState<PersonalRecord | null>(null);

  useEffect(() => {
    if (open) {
      loadPRs();
      setError('');
      setSuccess('');
    }
  }, [open, athlete.id]);

  const loadPRs = async () => {
    setLoading(true);
    try {
      // Pass teamId to allow head coaches to fetch PRs without permission issues
      const athletePRs = await avatarService.getUserPersonalRecords(athlete.id, teamId);
      setPRs(athletePRs);
    } catch (err: any) {
      setError('Failed to load personal records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPR = () => {
    setEditingPR(null);
    setPrDialogOpen(true);
  };

  const handleEditPR = (pr: PersonalRecord) => {
    setEditingPR(pr);
    setPrDialogOpen(true);
  };

  const handleVerifyPR = async (pr: PersonalRecord) => {
    try {
      await avatarService.updatePersonalRecord(pr.id, { isVerified: true });
      setSuccess('PR verified — counts on progression and leaderboards');
      await loadPRs();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to verify PR');
    }
  };

  const handleSavePR = async (prData: Partial<PersonalRecord>) => {
    if (!user) return;

    try {
      if (editingPR) {
        // Update existing PR
        await avatarService.updatePersonalRecord(editingPR.id, {
          ...prData,
          isVerified: true, // Coach verified
        });
        setSuccess('PR updated successfully');
      } else {
        // Add new PR for athlete — coach/head coach save = verified
        await avatarService.addPersonalRecord(athlete.id, teamId, {
          ...prData,
          eventType: prData.eventType!,
          time: prData.time!,
          date: prData.date!,
          isVerified: true,
        } as Omit<PersonalRecord, 'id' | 'userId' | 'teamId' | 'createdAt' | 'updatedAt'>);
        setSuccess('PR added successfully');
      }

      // Reload PRs
      await loadPRs();
      setPrDialogOpen(false);
      
      // Clear success message after delay
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save PR');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon />
            <Typography variant="h6">
              Personal Records - {athlete.displayName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Manage {athlete.displayName}'s personal records
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPR}
              size="small"
            >
              Add PR
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : prs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <TrophyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Personal Records Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add {athlete.displayName}'s first PR to get started
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prs.map((pr) => (
                    <TableRow key={pr.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {pr.eventType}
                        </Typography>
                        {pr.distance && (
                          <Typography variant="caption" color="text.secondary">
                            {pr.distance} miles
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" color="primary">
                          {formatPRTimeDisplay(pr.time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(pr.date.seconds * 1000).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {pr.location || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {pr.isVerified ? (
                          <Tooltip title="Coach Verified">
                            <Chip
                              icon={<VerifiedIcon />}
                              label="Verified"
                              size="small"
                              color="success"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="Unverified" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!pr.isVerified && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            sx={{ mr: 1, minWidth: 0, px: 1 }}
                            onClick={() => handleVerifyPR(pr)}
                          >
                            Verify
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleEditPR(pr)}
                          color="primary"
                          aria-label="Edit PR"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* PR Edit/Add Dialog */}
      <AddEditPRDialog
        open={prDialogOpen}
        onClose={() => setPrDialogOpen(false)}
        onSave={handleSavePR}
        existingPR={editingPR}
        isCoachEditing={true}
      />
    </>
  );
};

export default AthletePREditor;

